import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { supabase } from '../supabase';

// Estendendo interface Navigator para iOS standalone
interface ExtendedNavigator extends Navigator {
  standalone?: boolean;
}

// Estendendo o tipo de dispositivo para incluir informação de PWA e TWA
interface DeviceInfo {
  deviceId: string;
  userAgent: string;
  browser: string;
  os: string;
  type: string;
  timestamp: string;
  isPwa?: boolean;
  isTwa?: boolean;
}

// Configuração do Firebase
const firebaseConfig = {
  apiKey: "AIzaSyCQ9xSfp6OmGwXp_KipfV8N3vVlM79catM",
  authDomain: "boardverso-20.firebaseapp.com",
  projectId: "boardverso-20",
  storageBucket: "boardverso-20.firebasestorage.app",
  messagingSenderId: "280442518657",
  appId: "1:280442518657:web:223fdef252a261203ce180",
  measurementId: "G-01E9FBLEM2"
};

// VAPID Key para notificações web
const VAPID_KEY = 'BERUvBDDsvGfPhEB2WWqA__-Dwck331EP3UYT_tmTkG_GwppvDPz0aHnDJUwbvJ6j3fTzBxW2MwoTcgpdaZOXrI';

// Inicializar o Firebase
const app = initializeApp(firebaseConfig);

// Referência para a instância de messaging
let messaging: any = null;

// Flag para controlar operações em andamento
let isRequestingPermission = false;
let isRegisteringToken = false;

// Data da última verificação de token
const TOKEN_CHECK_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 dias

// Verificar se estamos em um ambiente TWA (Trusted Web Activity)
export function isTWA(): boolean {
  // Verificar User-Agent para assinatura Android
  const isAndroid = /Android/i.test(navigator.userAgent);
  
  // Verificar se temos parâmetros específicos de TWA na URL ou no localStorage
  const hasQueryParams = window.location.search.includes('twa=true') || 
                         window.location.search.includes('source=pwa');
  
  // Verificar o referenciador para ver se veio de um app Android
  const hasAndroidReferrer = document.referrer.includes('android-app://');
  
  // Verificar se temos algum storage com informação de TWA
  const hasTwaFlag = localStorage.getItem('is_twa') === 'true';
  
  // Verificar se temos cabeçalhos especiais que podem ser injetados pelo TWA
  const hasTwaHeader = document.querySelector('meta[name="twa-header"]') !== null;
  
  // Se for Android e tiver algum dos indicadores de TWA
  return isAndroid && (hasQueryParams || hasAndroidReferrer || hasTwaFlag || hasTwaHeader);
}

// Verificar se o navegador suporta Firebase Cloud Messaging
async function initializeMessaging() {
  try {
    const isMessagingSupported = await isSupported();
    
    if (isMessagingSupported) {
      messaging = getMessaging(app);
      console.log('[Messaging] Firebase Cloud Messaging inicializado com sucesso');
      return true;
    } else {
      console.warn('[Messaging] Este navegador não suporta Firebase Cloud Messaging');
      return false;
    }
  } catch (error) {
    console.error('[Messaging] Erro ao inicializar Firebase Cloud Messaging:', error);
    return false;
  }
}

// Registrar o Service Worker e solicitar permissão para notificações
export async function requestNotificationPermission() {
  // Evitar múltiplas solicitações de permissão simultâneas
  if (isRequestingPermission) {
    console.log('[Notifications] Já existe uma solicitação de permissão em andamento');
    return null;
  }
  
  isRequestingPermission = true;
  
  try {
    console.log('[Notifications] Iniciando processo de solicitação de permissão...');
    
    // Verificar se o navegador suporta notificações
    if (!('Notification' in window)) {
      console.warn('[Notifications] Este navegador não suporta notificações');
      isRequestingPermission = false;
      return null;
    }

    // Verificar se o dispositivo é móvel e se suporta notificações
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile && !('serviceWorker' in navigator)) {
      console.warn('[Notifications] Este dispositivo móvel não suporta notificações push');
      isRequestingPermission = false;
      return null;
    }

    // Verificar se estamos em TWA
    const isTwaEnv = isTWA();
    if (isTwaEnv) {
      console.log('[Notifications] Detectado ambiente TWA');
      localStorage.setItem('is_twa', 'true');
    }

    // Registrar o service worker ANTES de solicitar permissão
    if ('serviceWorker' in navigator) {
      try {
        console.log('[Notifications] Verificando registro do service worker...');
        
        // Para TWA, força atualização do service worker
        if (isTwaEnv) {
          // Verificar todos os registros existentes e forçar atualização
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            console.log('[Notifications] Atualizando service worker em TWA:', reg.scope);
            await reg.update();
          }
        }
        
        let registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        
        // Se não estiver registrado ou se estiver em estado de esperando
        if (!registration || registration.waiting || registration.installing) {
          console.log('[Notifications] Registrando service worker para notificações...');
          try {
            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
              scope: '/',
              updateViaCache: 'none' // Para TWA, garantir sempre verificação de atualização
            });
            
            // Aguardar até que o service worker esteja ativo
            if (registration && registration.installing) {
              console.log('[Notifications] Service worker está instalando, aguardando ativação...');
              
              await new Promise<void>((resolve) => {
                if (!registration) {
                  resolve();
                  return;
                }
                
                const worker = registration.installing;
                if (worker) {
                  worker.addEventListener('statechange', () => {
                    if (worker.state === 'activated') {
                      console.log('[Notifications] Service worker ativado com sucesso');
                      resolve();
                    }
                  });
                  
                  // Adicionar timeout para não ficar preso
                  setTimeout(() => {
                    console.log('[Notifications] Timeout na ativação do Service Worker');
                    resolve();
                  }, 5000);
                } else {
                  resolve(); // Caso o worker não exista mais, continuar
                }
              });
            }
            
            console.log('[Notifications] Service Worker registrado com sucesso:', registration);
          } catch (regError) {
            console.error('[Notifications] Erro ao registrar Service Worker:', regError);
            
            // Em dispositivos móveis (PWA ou TWA), tentar um fallback com escopo mais amplo
            const isMobileApp = isTwaEnv || 
                             window.matchMedia('(display-mode: standalone)').matches || 
                             ((window.navigator as any).standalone === true);
                           
            if (isMobileApp) {
              try {
                console.log('[Notifications] Tentando registro alternativo para app mobile...');
                registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                  scope: './',
                  updateViaCache: 'none'
                });
                console.log('[Notifications] Service Worker registrado para app mobile com escopo alternativo');
              } catch (mobileError) {
                console.error('[Notifications] Falha no registro para app mobile:', mobileError);
                throw mobileError; // Propagar erro para ser capturado pelo try/catch externo
              }
            } else {
              throw regError; // Propagar erro para ser capturado pelo try/catch externo
            }
          }
        } else {
          console.log('[Notifications] Service Worker já registrado:', registration);
          
          // Em TWA, forçar uma atualização do service worker
          if (isTwaEnv && registration.active) {
            console.log('[Notifications] Forçando atualização do Service Worker em TWA');
            try {
              await registration.update();
            } catch (updateError) {
              console.warn('[Notifications] Erro ao atualizar Service Worker em TWA:', updateError);
            }
          }
        }
      } catch (error) {
        console.error('[Notifications] Erro ao registrar Service Worker:', error);
        isRequestingPermission = false;
        return null;
      }
    }

    // Verificar se já temos permissão
    if (Notification.permission === 'granted') {
      console.log('[Notifications] Permissão já concedida, registrando token FCM');
      const token = await registerFCMToken();
      isRequestingPermission = false;
      return token;
    }

    // Solicitar permissão
    console.log('[Notifications] Solicitando permissão do navegador...');
    const permission = await Notification.requestPermission();
    console.log('[Notifications] Resposta da solicitação de permissão:', permission);
    
    if (permission === 'granted') {
      // Inicializar messaging após permissão concedida
      if (!messaging) {
        const initialized = await initializeMessaging();
        if (!initialized) {
          console.error('[Notifications] Falha ao inicializar messaging após obter permissão');
          isRequestingPermission = false;
          return null;
        }
      }
      
      // Agora tente obter o token FCM
      console.log('[Notifications] Permissão concedida, obtendo token FCM...');
      const token = await registerFCMToken();
      
      if (token) {
        console.log('[Notifications] Token FCM obtido com sucesso:', token);
        // Tentar enviar uma notificação de teste
        try {
          const testNotification = new Notification('Notificações ativadas!', {
            body: 'Você receberá notificações do Boardverso a partir de agora.',
            icon: '/AppImages/windows11/Square150x150Logo.scale-100.png',
            tag: 'welcome-notification'
          });
          console.log('[Notifications] Notificação de teste enviada');
        } catch (notifError) {
          console.warn('[Notifications] Não foi possível enviar notificação de teste:', notifError);
        }
      } else {
        console.error('[Notifications] Permissão concedida, mas falha ao obter token FCM');
      }
      
      isRequestingPermission = false;
      return token;
    } else {
      console.warn('[Notifications] Permissão para notificações negada pelo usuário');
      isRequestingPermission = false;
      return null;
    }
  } catch (error) {
    console.error('[Notifications] Erro ao solicitar permissão para notificações:', error);
    isRequestingPermission = false;
    return null;
  }
}

// Registrar o token FCM no Supabase
async function registerFCMToken() {
  // Evitar múltiplos registros simultâneos
  if (isRegisteringToken) {
    console.log('[Notifications] Já existe um registro de token em andamento');
    return null;
  }
  
  isRegisteringToken = true;
  
  if (!messaging) {
    const initialized = await initializeMessaging();
    if (!initialized) {
      isRegisteringToken = false;
      return null;
    }
  }

  try {
    // Detectar se estamos em um ambiente PWA ou TWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                 ((window.navigator as any).standalone === true);
    const isTwaEnv = isTWA();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isTwaEnv) {
      console.log('[Notifications] Detectado ambiente TWA para registro de token');
    } else if (isPWA && isMobile) {
      console.log('[Notifications] Detectado ambiente PWA em dispositivo móvel');
    }
    
    // Obter o token do FCM com retry
    let currentToken = null;
    let retryCount = 0;
    const maxRetries = isTwaEnv ? 5 : (isPWA ? 4 : 3); // Mais tentativas para TWA e PWA
    const delayBetweenRetries = isTwaEnv ? 2500 : (isPWA ? 2000 : 1000); // Esperar mais tempo em ambientes móveis
    
    while (!currentToken && retryCount < maxRetries) {
      try {
        console.log(`[Notifications] Tentativa ${retryCount + 1} de obter token FCM...`);
        
        // Garantir que o messaging esteja inicializado antes de cada tentativa
        if (!messaging) {
          await initializeMessaging();
          if (!messaging) {
            console.error('[Notifications] Messaging não inicializado na tentativa', retryCount + 1);
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
            continue;
          }
        }
        
        // Configuração especial para TWA/PWA móvel
        const tokenOptions = {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: undefined as ServiceWorkerRegistration | undefined
        };
        
        // Para TWA/PWA móvel, tentar obter o registration diretamente
        if ((isTwaEnv || isPWA) && isMobile) {
          try {
            // Priorizar o service worker com escopo específico para FCM
            let swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
            if (!swReg) {
              // Se não encontrar o específico, tente qualquer um
              const regs = await navigator.serviceWorker.getRegistrations();
              if (regs.length > 0) {
                swReg = regs[0]; // Pegar o primeiro disponível
              }
            }
            
            if (swReg) {
              console.log('[Notifications] Usando registration explícito para token em app mobile:', swReg.scope);
              tokenOptions.serviceWorkerRegistration = swReg;
            }
          } catch (swErr) {
            console.warn('[Notifications] Não foi possível obter registration para token:', swErr);
            // Continuar com as opções padrão
          }
        }
        
        // Em ambiente TWA, adicione tempo extra antes de tentar obter o token
        if (isTwaEnv && retryCount > 0) {
          console.log('[Notifications] Aguardando estabilização do TWA antes de obter token...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        currentToken = await getToken(messaging, tokenOptions);
        
        if (!currentToken) {
          console.warn(`[Notifications] Tentativa ${retryCount + 1}: Não foi possível obter um token FCM`);
          retryCount++;
          // Delay entre tentativas
          await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
        }
      } catch (tokenError) {
        console.error(`[Notifications] Erro na tentativa ${retryCount + 1} de obter token:`, tokenError);
        retryCount++;
        // Delay maior após erro
        await new Promise(resolve => setTimeout(resolve, delayBetweenRetries * 1.5));
      }
    }

    if (!currentToken) {
      console.error('[Notifications] Falha em todas as tentativas de obter token FCM');
      isRegisteringToken = false;
      return null;
    }

    console.log('[Notifications] Token FCM obtido com sucesso:', currentToken);

    // Obter informações do dispositivo atual
    const deviceInfo = getDeviceInfo();
    deviceInfo.isPwa = isPWA;
    deviceInfo.isTwa = isTwaEnv;

    // Salvar o token no Supabase
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.error('[Notifications] Usuário não autenticado:', userError);
      // Em TWA/PWA mobile, armazenar o token localmente mesmo sem estar autenticado
      if (isTwaEnv || isPWA) {
        localStorage.setItem('fcm_token_pending', currentToken);
        console.log('[Notifications] Token salvo localmente para sincronização futura');
      }
      isRegisteringToken = false;
      return currentToken; // Retornar o token mesmo assim para app móvel
    }
    
    const user = userData.user;

    // Verificar se o token já está registrado para este dispositivo
    const { data: existingToken, error: checkError } = await supabase
      .from('device_tokens')
      .select('*')
      .eq('user_id', user.id)
      .eq('device_id', deviceInfo.deviceId)
      .maybeSingle();

    if (checkError) {
      console.error('Erro ao verificar token existente:', checkError);
    }

    // Se o token existe e é diferente do atual, atualize-o
    if (existingToken) {
      if (existingToken.token !== currentToken) {
        try {
          const { error: updateError } = await supabase
            .from('device_tokens')
            .update({ 
              token: currentToken,
              last_updated: new Date().toISOString(),
              device_info: deviceInfo
            })
            .eq('id', existingToken.id);
          
          if (updateError) {
            console.error('Erro ao atualizar token FCM:', updateError);
            isRegisteringToken = false;
            return null;
          }
          console.log('Token FCM atualizado com sucesso');
        } catch (dbError) {
          console.error('Exceção ao atualizar token:', dbError);
          isRegisteringToken = false;
          return null;
        }
      }
    } else {
      // Se não existe, crie um novo registro
      try {
        const { error: insertError } = await supabase
          .from('device_tokens')
          .insert({
            user_id: user.id,
            token: currentToken,
            device_id: deviceInfo.deviceId,
            device_info: deviceInfo,
            created_at: new Date().toISOString(),
            last_updated: new Date().toISOString()
          });
        
        if (insertError) {
          console.error('Erro ao salvar token FCM:', insertError);
          isRegisteringToken = false;
          return null;
        }
        console.log('Novo token FCM registrado com sucesso');
      } catch (dbError) {
        console.error('Exceção ao inserir token:', dbError);
        isRegisteringToken = false;
        return null;
      }
    }

    // Também mantenha o token no perfil do usuário para compatibilidade
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ fcm_token: currentToken })
        .eq('id', user.id);

      if (profileError) {
        console.error('Erro ao atualizar token FCM no perfil:', profileError);
      } else {
        console.log('Token FCM atualizado no perfil do usuário');
      }
    } catch (profileError) {
      console.error('Exceção ao atualizar perfil:', profileError);
    }

    // Armazenar data da última verificação
    localStorage.setItem('lastTokenCheck', Date.now().toString());
    // Armazenar o token para verificação rápida
    localStorage.setItem('current_fcm_token', currentToken);
    
    // Para TWA, armazenar flag específica
    if (isTwaEnv) {
      localStorage.setItem('is_twa', 'true');
      localStorage.setItem('twa_fcm_token', currentToken);
    }

    isRegisteringToken = false;
    return currentToken;
  } catch (error) {
    console.error('Erro ao registrar token FCM:', error);
    isRegisteringToken = false;
    return null;
  }
}

// Função para obter informações do dispositivo atual
function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const browserName = detectBrowser();
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const os = detectOS();
  const isTwaEnv = isTWA();
  
  // Gerar um ID único para este dispositivo/navegador
  let deviceId = localStorage.getItem('fcm_device_id');
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem('fcm_device_id', deviceId);
  }
  
  return {
    deviceId,
    userAgent: ua,
    browser: browserName,
    os,
    type: isMobile ? 'mobile' : 'desktop',
    timestamp: new Date().toISOString(),
    isPwa: false, // Valor padrão
    isTwa: isTwaEnv
  };
}

// Detectar navegador
function detectBrowser() {
  const ua = navigator.userAgent;
  if (ua.indexOf('Chrome') > -1) return 'Chrome';
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  if (ua.indexOf('Safari') > -1) return 'Safari';
  if (ua.indexOf('Edge') > -1) return 'Edge';
  if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) return 'IE';
  return 'Unknown';
}

// Detectar sistema operacional
function detectOS() {
  const ua = navigator.userAgent;
  if (ua.indexOf('Win') > -1) return 'Windows';
  if (ua.indexOf('Mac') > -1) return 'MacOS';
  if (ua.indexOf('Linux') > -1) return 'Linux';
  if (ua.indexOf('Android') > -1) return 'Android';
  if (ua.indexOf('iOS') > -1 || /iPhone|iPad|iPod/.test(ua)) return 'iOS';
  return 'Unknown';
}

// Gerar UUID v4 para identificar dispositivos
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Configurar handler para mensagens em primeiro plano
export function setupForegroundNotifications(callback: (payload: any) => void) {
  if (!messaging) {
    initializeMessaging().then(initialized => {
      if (initialized) {
        onMessage(messaging, callback);
      }
    });
    return;
  }

  onMessage(messaging, callback);
}

// Verificar token e registrar novamente se necessário
export async function checkAndUpdateFCMToken() {
  // Verificar se o usuário está logado
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  
  // Verificar se estamos em ambiente TWA
  const isTwaEnv = isTWA();
  if (isTwaEnv) {
    localStorage.setItem('is_twa', 'true');
    console.log('[Tokens] Detectado ambiente TWA durante verificação de token');
  }
  
  // Verificar se passou tempo suficiente desde a última verificação
  const lastTokenCheck = localStorage.getItem('lastTokenCheck');
  const now = Date.now();
  
  // Se verificamos recentemente, não precisamos verificar novamente, exceto em TWA
  if (lastTokenCheck && (now - parseInt(lastTokenCheck) < TOKEN_CHECK_INTERVAL) && !isTwaEnv) {
    console.log('Token FCM verificado recentemente, pulando verificação');
    
    // Verificar se temos um token armazenado localmente
    const { data: profile } = await supabase
      .from('profiles')
      .select('fcm_token')
      .eq('id', user.id)
      .single();
      
    return profile?.fcm_token || null;
  }

  console.log('Verificando token FCM...');

  // Obter o token atual do dispositivo
  const deviceId = localStorage.getItem('fcm_device_id');
  
  if (!deviceId) {
    // Se não temos um ID de dispositivo, este é provavelmente um novo dispositivo
    return await requestNotificationPermission();
  }
  
  // Verificar se o token está armazenado no banco de dados
  const { data: deviceToken, error: deviceTokenError } = await supabase
    .from('device_tokens')
    .select('token, last_updated')
    .eq('user_id', user.id)
    .eq('device_id', deviceId)
    .maybeSingle();
    
  if (deviceTokenError) {
    console.error('Erro ao buscar token do dispositivo:', deviceTokenError);
  }
  
  // Verificar permissão atual do navegador
  if (Notification.permission !== 'granted') {
    console.log('Permissões de notificação não concedidas');
    return null;
  }
  
  // Para TWA, sempre fazer uma verificação completa
  if (isTwaEnv) {
    console.log('Forçando verificação completa de token em ambiente TWA');
    return await requestNotificationPermission();
  }
  
  // Se não temos um token para este dispositivo ou se passou muito tempo desde a última atualização
  if (!deviceToken || !deviceToken.token) {
    console.log('Token FCM não encontrado para este dispositivo, solicitando novo token');
    return await requestNotificationPermission();
  }
  
  // Se temos um token, verificar se ainda é válido
  if (await initializeMessaging()) {
    try {
      const currentToken = await getToken(messaging, {
        vapidKey: VAPID_KEY
      });

      // Se o token mudou, atualizá-lo
      if (currentToken && currentToken !== deviceToken.token) {
        console.log('Token FCM mudou, atualizando no banco de dados');
        
        const deviceInfo = getDeviceInfo();
        
        // Atualizar na tabela device_tokens
        const { error: updateError } = await supabase
          .from('device_tokens')
          .update({ 
            token: currentToken,
            last_updated: new Date().toISOString(),
            device_info: deviceInfo
          })
          .eq('user_id', user.id)
          .eq('device_id', deviceId);
          
        if (updateError) {
          console.error('Erro ao atualizar token do dispositivo:', updateError);
        }
        
        // Atualizar também no perfil para compatibilidade
        await supabase
          .from('profiles')
          .update({ fcm_token: currentToken })
          .eq('id', user.id);
          
        // Atualizar data da última verificação
        localStorage.setItem('lastTokenCheck', now.toString());
        
        return currentToken;
      }
      
      // O token ainda é válido, atualizar apenas a data da última verificação
      localStorage.setItem('lastTokenCheck', now.toString());
      return deviceToken.token;
    } catch (error) {
      console.error('Erro ao verificar token FCM:', error);
      return null;
    }
  }

  return deviceToken.token;
}

// Adiciona verificação quando o dispositivo voltar a ficar online
export function setupOfflineTokenRecovery() {
  if (typeof window !== 'undefined') {
    window.addEventListener('online', async () => {
      console.log('Dispositivo voltou online, verificando token FCM...');
      await checkAndUpdateFCMToken();
    });
    
    // Se estamos em um ambiente TWA, verificar sempre ao receber foco
    if (isTWA()) {
      window.addEventListener('focus', async () => {
        console.log('Ambiente TWA recebeu foco, verificando token FCM...');
        await checkAndUpdateFCMToken();
      });
    }
  }
} 